import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Search, SlidersHorizontal, X } from 'lucide-react';
import { EventCard } from '../components/events/EventCard';
import { GenreFilter } from '../components/events/GenreFilter';
import { CityFilter } from '../components/events/CityFilter';
import { TypeFilter } from '../components/events/TypeFilter';
import { SEOHead } from '../components/SEOHead';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Catalog = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [events, setEvents] = useState([]);
  const [genres, setGenres] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const selectedGenre = searchParams.get('genre');
  const selectedCity = searchParams.get('city');
  const selectedType = searchParams.get('type');
  const smartOnly = searchParams.get('smart') === 'true';

  useEffect(() => {
    fetchFilters();
  }, []);

  useEffect(() => {
    fetchEvents();
  }, [selectedGenre, selectedCity, selectedType, smartOnly]);

  const fetchFilters = async () => {
    try {
      const [genresRes, citiesRes] = await Promise.all([
        axios.get(`${API}/genres`),
        axios.get(`${API}/cities`)
      ]);
      setGenres(genresRes.data);
      setCities(citiesRes.data);
    } catch (error) {
      console.error('Error fetching filters:', error);
    }
  };

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedGenre) params.append('genre', selectedGenre);
      if (selectedCity) params.append('city', selectedCity);
      if (selectedType) params.append('event_type', selectedType);
      if (smartOnly) params.append('smart', 'true');
      // Exclude past events
      const today = new Date().toISOString().split('T')[0];
      params.append('date_from', today);

      const response = await axios.get(`${API}/events?${params.toString()}`);
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateFilter = (key, value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    setSearchParams(newParams);
  };

  const clearFilters = () => {
    setSearchParams({});
    setSearchTerm('');
  };

  const hasActiveFilters = selectedGenre || selectedCity || selectedType || smartOnly;

  const filteredEvents = events.filter(event =>
    event.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    event.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    event.venue.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const genreLabels = {
    opera: 'Ópera',
    ballet: 'Ballet',
    concierto: 'Conciertos',
    teatro: 'Teatro',
    musical: 'Musicales',
    cine: 'Cine'
  };

  return (
    <div className="min-h-screen pt-24" data-testid="catalog-page">
      <SEOHead 
        title={smartOnly ? 'Eventos con Descuento SMART' : 'Catálogo de Eventos'}
        description={smartOnly 
          ? 'Eventos con venta SMART activa. Regístrate gratis y obtén hasta un 25% de descuento en entradas de ópera, ballet y conciertos en cines.'
          : 'Explora nuestro catálogo completo de ópera, ballet, conciertos y teatro en cines de toda España. Filtra por género, ciudad o tipo de proyección.'
        }
        keywords={`catálogo eventos culturales, ${selectedGenre || 'ópera ballet conciertos'}, ${selectedCity || 'cines España'}, entradas teatro, programación cultural`}
        canonicalUrl="/catalogo"
      />
      
      {/* Header */}
      <div className="section-container py-12 border-b border-white/5">
        <span className="text-[var(--primary)] text-xs uppercase tracking-[0.3em] block mb-4">
          {smartOnly ? 'Venta SMART' : 'Descubre'}
        </span>
        <h1 className="font-['Playfair_Display'] text-5xl md:text-6xl text-white mb-6">
          {smartOnly ? 'Eventos con Descuento' : 'Catálogo de Eventos'}
        </h1>
        <p className="text-white/50 max-w-2xl">
          {smartOnly 
            ? 'Eventos con venta SMART activa. Regístrate gratis y obtén hasta un 25% de descuento en tus entradas.'
            : 'Explora nuestra selección de eventos culturales. Filtra por género, ciudad o tipo de proyección.'}
        </p>
        {smartOnly && (
          <button 
            onClick={() => updateFilter('smart', null)}
            className="mt-4 text-[var(--primary)] text-sm flex items-center gap-2 hover:underline"
          >
            <X size={14} /> Ver todos los eventos
          </button>
        )}
      </div>

      {/* Filters Bar */}
      <div className="section-container py-6 border-b border-white/5 sticky top-20 bg-[var(--background)] z-30">
        <div className="flex flex-col lg:flex-row gap-6 items-start lg:items-center justify-between">
          {/* Search */}
          <div className="relative w-full lg:w-80">
            <Search className="absolute left-0 top-1/2 -translate-y-1/2 text-white/30" size={18} />
            <input
              type="text"
              placeholder="Buscar eventos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-8 pr-4 py-2 bg-transparent border-b border-white/20 focus:border-[var(--primary)] text-white placeholder:text-white/30 outline-none"
              data-testid="search-input"
            />
          </div>

          {/* Filter Controls */}
          <div className="flex items-center gap-4">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`btn-ghost flex items-center gap-2 ${showFilters ? 'text-[var(--primary)]' : ''}`}
              data-testid="toggle-filters"
            >
              <SlidersHorizontal size={16} />
              Filtros
            </button>

            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="btn-ghost flex items-center gap-2 text-white/50 hover:text-white"
                data-testid="clear-filters"
              >
                <X size={16} />
                Limpiar
              </button>
            )}

            <span className="text-white/30 text-sm">
              {filteredEvents.length} eventos
            </span>
          </div>
        </div>

        {/* Expandable Filters */}
        {showFilters && (
          <div className="mt-6 pt-6 border-t border-white/5 animate-fadeIn" data-testid="filters-panel">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {/* Genre Filter */}
              <div>
                <label className="text-xs uppercase tracking-widest text-white/50 mb-4 block">
                  Género
                </label>
                <GenreFilter
                  genres={genres}
                  selectedGenre={selectedGenre}
                  onGenreChange={(value) => updateFilter('genre', value)}
                />
              </div>

              {/* City Filter */}
              <div>
                <label className="text-xs uppercase tracking-widest text-white/50 mb-4 block">
                  Ciudad
                </label>
                <CityFilter
                  cities={cities}
                  selectedCity={selectedCity}
                  onCityChange={(value) => updateFilter('city', value)}
                />
              </div>

              {/* Type Filter */}
              <div>
                <label className="text-xs uppercase tracking-widest text-white/50 mb-4 block">
                  Tipo
                </label>
                <TypeFilter
                  selectedType={selectedType}
                  onTypeChange={(value) => updateFilter('type', value)}
                />
              </div>
            </div>
          </div>
        )}

        {/* Active Filters Tags */}
        {hasActiveFilters && (
          <div className="mt-4 flex flex-wrap gap-2" data-testid="active-filters">
            {selectedGenre && (
              <span className="inline-flex items-center gap-2 px-3 py-1 bg-[var(--primary)]/10 text-[var(--primary)] text-xs">
                {genreLabels[selectedGenre] || selectedGenre}
                <button onClick={() => updateFilter('genre', null)}>
                  <X size={12} />
                </button>
              </span>
            )}
            {selectedCity && (
              <span className="inline-flex items-center gap-2 px-3 py-1 bg-[var(--primary)]/10 text-[var(--primary)] text-xs">
                {selectedCity}
                <button onClick={() => updateFilter('city', null)}>
                  <X size={12} />
                </button>
              </span>
            )}
            {selectedType && (
              <span className="inline-flex items-center gap-2 px-3 py-1 bg-[var(--primary)]/10 text-[var(--primary)] text-xs">
                {selectedType === 'directo' ? 'En Directo' : 'Grabado'}
                <button onClick={() => updateFilter('type', null)}>
                  <X size={12} />
                </button>
              </span>
            )}
          </div>
        )}
      </div>

      {/* Events Grid */}
      <div className="section-container section-spacing">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="card-event">
                <div className="aspect-[4/3] skeleton" />
                <div className="p-6 space-y-4">
                  <div className="h-6 skeleton w-3/4" />
                  <div className="h-4 skeleton w-full" />
                  <div className="h-4 skeleton w-2/3" />
                </div>
              </div>
            ))}
          </div>
        ) : filteredEvents.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="events-grid">
            {filteredEvents.map((event) => (
              <EventCard key={event.id} event={event} />
            ))}
          </div>
        ) : (
          <div className="text-center py-20" data-testid="no-events">
            <p className="text-white/50 text-lg mb-4">
              No se encontraron eventos con los filtros seleccionados.
            </p>
            <button onClick={clearFilters} className="btn-outline">
              Limpiar filtros
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Catalog;
